import { Button, createStyles, DialogContent, DialogTitle, makeStyles, TextField, Theme } from '@material-ui/core';
import React, { useReducer } from 'react';
import { Node } from 'slate';

import { PostDetailsFragment, useGetDocumentToImportQuery, useUpdatePostMutation } from '../../../../graphql/gen/types';
import { notEmpty } from '../../../../utils/notEmpty';
import { logAnalytics } from '../../shared/logAnalytics';
import { DialogActions } from '../DialogActions';
import { useDialogState } from '../useDialogState';
import { documentToImportReducer } from './documentToImportReducer';
import { getSelectedContent } from './getSelectedContent';
import { PostGoogleDocPreview } from './PostGoogleDocPreview';
import { useInitializeDocumentToImportOnSuccess } from './useInitializeDocumentToImportOnSuccess';

type Props = {
  post: PostDetailsFragment;
};

export const SyncPostFromGoogleDocs = ({ post }: Props): React.ReactElement => {
  if (post.source == null) {
    throw new Error(`Invalid SyncPostFromGoogleDocs for post without source`);
  }
  const { url, headingIds } = post.source;

  const classes = useStyles();
  const { data, loading } = useGetDocumentToImportQuery({ variables: { url }, fetchPolicy: 'no-cache' });
  const [documentToImport, dispatch] = useReducer(documentToImportReducer, undefined);
  const [, setDialogState] = useDialogState();
  const [updatePostMutation, { loading: updatePostLoading }] = useUpdatePostMutation();

  useInitializeDocumentToImportOnSuccess({ data, dispatch, headingIds });

  const handleUpdate = async (): Promise<void> => {
    if (documentToImport != null) {
      const { selectedSections, document, content, externalEntityId } = documentToImport;
      const { titleSection, selectedIndexes } = getSelectedContent({
        document,
        selectedSections: [...selectedSections],
      });
      const { data } = await updatePostMutation({
        variables: {
          post: {
            id: post.id,
            title: Node.string((titleSection.title as unknown) as Node),
            body: content.filter((_, index) => selectedIndexes[index]),
            publishStatus: post.publishStatus,
            labelIds: post.labels.map((label) => label.id),
            source: {
              externalEntityId,
              headingIds: [...selectedSections].map((section) => section.title.headingId).filter(notEmpty),
            },
          },
        },
      });

      if (data?.updatePost?.id != null) {
        logAnalytics({ name: 'post synced gdoc', details: { plexId: post.plex.id, itemId: post.id } });
        setDialogState(null, 'replace');
      }
    }
  };

  return (
    <div className={classes.root}>
      <DialogTitle id="simple-dialog-title">Sync Post from Google Docs</DialogTitle>
      <DialogContent>
        <TextField
          value={post.source.url}
          disabled
          fullWidth
          variant="outlined"
          label="Google document link"
          name="Google document link"
        />

        <PostGoogleDocPreview dispatch={dispatch} documentToImport={documentToImport} data={data} loading={loading} />
      </DialogContent>

      <DialogActions
        cta={
          <Button variant="contained" color="primary" disabled={loading || updatePostLoading} onClick={handleUpdate}>
            Sync
          </Button>
        }
      />
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
  })
);
